import type { FlowComposerDraft } from './flow-composer.ts';
import type { FlowViewType } from './views.ts';

export type ToolReadinessStatus = 'draft' | 'publishable';

export type ToolReadinessCheckId =
  | 'clear_promise'
  | 'clear_inputs'
  | 'display_ready_output'
  | 'stable_output_contract'
  | 'user_facing_language';

export interface ToolReadinessCheck {
  id: ToolReadinessCheckId;
  label: string;
  passed: boolean;
  blocking: boolean;
  message: string;
}

export interface ToolReadinessResult {
  status: ToolReadinessStatus;
  publishable: boolean;
  checks: ToolReadinessCheck[];
  blockingMessages: string[];
}

const DISPLAY_READY_ACTIONS = new Set([
  'analysis.insight',
  'content.synthesize',
  'llm.extract',
  'llm.prompt',
]);

const DIAGNOSTIC_VIEW_TYPES: FlowViewType[] = [
  'score_card',
  'health_gauge',
  'metric_grid',
];

const TECHNICAL_COPY_PATTERNS = [
  /\bartefacts?\b/i,
  /\bcontrat\b/i,
  /\bdominos?\b/i,
  /\bjson\b/i,
  /\bpayload\b/i,
  /\bprimitive\b/i,
  /\bschema\b/i,
  /\bstrictness\b/i,
  /\bworkflow\b/i,
  /\boutput[_ -]?contract\b/i,
];

const hasMeaningfulText = (value: string, minLength: number): boolean => (
  value.trim().replace(/\s+/g, ' ').length >= minLength
);

const containsTechnicalCopy = (value: string): boolean => (
  TECHNICAL_COPY_PATTERNS.some((pattern) => pattern.test(value))
);

const createCheck = (
  params: Omit<ToolReadinessCheck, 'blocking'> & { blocking?: boolean },
): ToolReadinessCheck => ({
  ...params,
  blocking: params.blocking ?? true,
});

export const evaluateToolReadiness = (
  draft: FlowComposerDraft,
): ToolReadinessResult => {
  const title = draft.title.trim();
  const description = draft.description.trim();
  const outputDescription = draft.outputDescription.trim();
  const lastDomino = draft.dominos[draft.dominos.length - 1] ?? null;
  const inputLabelsAreClear = draft.inputs.every((input) => (
    hasMeaningfulText(input.key, 2)
    && hasMeaningfulText(input.label, 2)
    && Boolean(input.type)
  ));
  const finalOutputIsDisplayReady = lastDomino
    ? DISPLAY_READY_ACTIONS.has(lastDomino.actionType)
    : false;
  const needsMetricContract = DIAGNOSTIC_VIEW_TYPES.includes(draft.viewType);
  const hasStableOutputContract = !needsMetricContract
    || draft.outputContract.metrics.length > 0
    || hasMeaningfulText(outputDescription, 60);
  const visibleCopy = [
    title,
    description,
    draft.assistantSummary,
    outputDescription,
  ].join('\n');

  const checks: ToolReadinessCheck[] = [
    createCheck({
      id: 'clear_promise',
      label: 'Promesse claire',
      passed: hasMeaningfulText(title, 3)
        && hasMeaningfulText(description, 20)
        && hasMeaningfulText(outputDescription, 20),
      message: 'La promesse, la description et le résultat attendu doivent être compréhensibles sans jargon.',
    }),
    createCheck({
      id: 'clear_inputs',
      label: 'Champs lisibles',
      passed: inputLabelsAreClear,
      message: 'Les champs demandés au lancement doivent avoir des libellés clairs.',
    }),
    createCheck({
      id: 'display_ready_output',
      label: 'Résultat affichable',
      passed: finalOutputIsDisplayReady,
      message: 'La dernière étape doit produire le résultat métier affiché à l’utilisateur.',
    }),
    createCheck({
      id: 'stable_output_contract',
      label: 'Format de résultat stable',
      passed: hasStableOutputContract,
      message: 'Les tools de diagnostic doivent prévoir au moins un signal stable ou une description de résultat suffisamment précise.',
    }),
    createCheck({
      id: 'user_facing_language',
      label: 'Langage utilisateur',
      passed: !containsTechnicalCopy(visibleCopy),
      message: 'Les textes visibles doivent parler du résultat métier, pas du fonctionnement interne.',
    }),
  ];

  const blockingMessages = checks
    .filter((check) => check.blocking && !check.passed)
    .map((check) => check.message);
  const publishable = blockingMessages.length === 0;

  return {
    status: publishable ? 'publishable' : 'draft',
    publishable,
    checks,
    blockingMessages,
  };
};
